import { i18n } from "../i18n/i18n";

import React, { Component } from 'react'
import shipOrderApi, { ShipOrderData } from '../apis/shipOrder.api'
import palletApi, { PalletData } from '../apis/pallet.api'
import shipApi, { ShipCreateRequest } from '../apis/ship.api'
import shipOrderService from '../services/shipOrder.service'
import geolocationService from '../services/geolocation.service'
import confirmService from '../services/confirm.service'
import alertService from '../services/alert.service'
import { ConfirmParams } from '../components/Confirm/Confirm.state'
import Card from '../components/Card'
import PageHeading from '../components/PageHeading'
import Row from '../components/Row/Row'
import SearchPicker, { SearchPickerOption } from '../components/SearchPicker'
import ItemCard from '../components/ItemCard'
import Checkbox from '../components/Checkbox'
import CardButton from '../components/CardButton'
import InputScan from '../components/InputScan'

interface ShipFormProps {
  onShipOrderSelect: (id: number | null) => void
  onReset: () => void
}

interface ShipFormState {
  shipOrder: string
  shipOrderId: number | null
  palletCode: string
  autoPick: boolean
  pallet: PalletData | null
  shipOrders: ShipOrderData[]
  latitude: number | null
  longitude: number | null
}

export default class ShipForm extends Component<ShipFormProps, ShipFormState> {
  constructor (props: ShipFormProps) {
    super(props)
    this.state = {
      shipOrder: '',
      shipOrderId: null,
      palletCode: '',
      autoPick: true,
      pallet: null,
      shipOrders: [],
      latitude: null,
      longitude: null
    }
    this.handleShipOrderSearch = this.handleShipOrderSearch.bind(this)
    this.handleInputChange = this.handleInputChange.bind(this)
    this.handleInputScan = this.handleInputScan.bind(this)
    this.handleShipOrderSelect = this.handleShipOrderSelect.bind(this)
    this.handleShipOrderBlur = this.handleShipOrderBlur.bind(this)
    this.handleCheckboxChange = this.handleCheckboxChange.bind(this)
    this.handleShipClick = this.handleShipClick.bind(this)
  }

  get shipOrderSearchOptions () {
    return shipOrderService.searchOptions(this.state.shipOrders)
  }

  get palletItem () {
    const { pallet } = this.state
    if (!pallet || !pallet.item) return null
    const { item } = pallet
    return (
      <div className="mb-4">
        <ItemCard
          heading="Scanned item"
          code={item.code}
          description={item.description}
          cpp={item.cpp}
          qpc={item.qpc}
          quantity={pallet.quantity} />
      </div>
    )
  }

  get button () {
    return !this.state.autoPick &&
      <CardButton
        proceedText={i18n("Pick to Ship")}
        onProceedClick={this.handleShipClick} />
  }

  readGeolocation () {
    geolocationService.access(coordinate => {
      if (!coordinate) return
      const { latitude, longitude } = coordinate
      this.setState({ latitude, longitude })
    })
  }

  readPalletByCode (code: string) {
    if (!code) {
      this.setState({ pallet: null })
      return
    }
    palletApi.readByCode(code, pallet => {
      if (!pallet) return
      this.setState({ pallet }, this.autoCreateShip)
    })
  }

  reset () {
    this.setState({
      palletCode: '',
      pallet: null,
      shipOrders: []
    })
    const { shipOrderId } = this.state
    this.props.onShipOrderSelect(shipOrderId)
  }

  createShip () {
    const { shipOrderId, pallet, latitude, longitude } = this.state
    const payload: ShipCreateRequest = {}
    if (shipOrderId) payload.shipOrder = shipOrderId
    if (pallet) payload.pallet = pallet.id
    if (latitude) payload.latitude = latitude
    if (longitude) payload.longitude = longitude
    shipApi.create(payload, ship => {
      if (!ship) return
      alertService.success('Successfully picked to ship')
      this.reset()
    })
  }

  autoCreateShip () {
    if (this.state.autoPick) this.createShip()
  }

  handleInputChange (name: string, value: string) {
    if (name === 'shipOrder') {
      this.props.onShipOrderSelect(null)
      this.setState({ shipOrderId: null, shipOrder: value })
    } else if (name === 'palletCode') {
      this.setState({ palletCode: value })
    }
  }

  handleInputScan (name: string, value: string) {
    if (name === 'palletCode') {
      this.readPalletByCode(value)
    }
  }

  handleShipOrderSearch (name: string, code: string) {
    shipOrderApi.searchByCode(code, shipOrders => {
      if (!shipOrders) return
      this.setState({ shipOrders })
    })
  }

  handleShipOrderSelect (name: string, option: SearchPickerOption) {
    const shipOrderId = option.id as number
    this.setState({ shipOrderId, shipOrders: [] })
    this.props.onShipOrderSelect(shipOrderId)
  }

  handleShipOrderBlur (name: string) {
    this.setState({ shipOrders: [] })
  }

  handleCheckboxChange (name: string) {
    if (name === 'autoPick') {
      this.setState(state => {
        const autoPick = !state.autoPick
        return { autoPick }
      })
    }
  }

  handleShipClick () {
    const confirm: ConfirmParams = {
      id: 1,
      question: i18n('Please review ship details above and then confirm'),
      confirmText: i18n('Confirm'),
      cancelText: i18n('Cancel'),
      onConfirm: () => this.createShip()
    }
    confirmService.push(confirm)
  }

  componentDidMount () {
    this.readGeolocation()
  }

  render () {
    return (
      <Card>
        <PageHeading
          text={i18n("Ship")}
          doc='ship' />
        <Row column={2}>
          <SearchPicker
            name="shipOrder"
            value={this.state.shipOrder}
            options={this.shipOrderSearchOptions}
            label={i18n("Ship order")}
            onSearch={this.handleShipOrderSearch}
            onChange={this.handleInputChange}
            onSelect={this.handleShipOrderSelect}
            onBlur={this.handleShipOrderBlur} />
          <InputScan
            name="palletCode"
            value={this.state.palletCode}
            label={i18n("Scan item code")}
            onChange={this.handleInputChange}
            onScan={this.handleInputScan} />
          {this.palletItem}
        </Row>
        <Row column={1}>
          <div className="mb-4">
            <Checkbox
              name="autoPick"
              label="Auto pick to ship"
              checked={this.state.autoPick}
              onChange={this.handleCheckboxChange} />
          </div>
        </Row>
        {this.button}
      </Card>
    )
  }
}
